/**
 * Caps: a crowd throws mortarboards in the air. Two or three quick volleys go
 * up from just below the window, tumble to somewhere in the top half, glint
 * at the top of the throw, and flutter back down out of sight.
 */

import { calm, pick, rand, scene, type Frame } from "./scene";

/** Navy rather than black, with a lighter edge and rim, so a cap still reads on the dark theme. */
const BOARD = "#1f2739";
const BOARD_UNDER = "#38445e";
const BOARD_EDGE = "#56648a";
const RIM = "rgba(165, 182, 218, 0.6)";
const SKULL_DARK = "#121826";
const SKULL_LIGHT = "#2e3852";
const SKULL_INSIDE = "#07090e";
const GOLD = "#e8b437";
const GOLD_BRIGHT = "#fbdc78";
/** Outlines the gold, so the tassel and sparkles don't wash out on the light theme. */
const GOLD_DEEP = "#a8740f";

/** The cap's shape, in board widths. */
const THICK = 0.05;
const SKULL_R = 0.34;
const SKULL_H = 0.26;
const BUTTON_H = 0.04;
const CORD = 0.6;
const TUFT = 0.3;
/** The furthest any part of a cap reaches above its centre: it starts and ends this far below the window, so it's never seen cut off. */
const REACH = 0.8;

/** When each volley goes (ms), and how spread out the throws in one are, so it's a crowd rather than a cannon. */
const VOLLEYS = [0, 380, 760];
const CALM_VOLLEYS = [0, 500];
const STAGGER = 140;
/** Gravity on the way up, in window heights per second squared: the highest throw is back out of sight at about 5 s. */
const RISE_G = 0.75;
/** A cap falls on this fraction of the gravity it rose against: the air holds it up on the way down. */
const FALL_G = 0.42;
/** How fast the tumbling dies away once it's falling, per second. */
const SETTLE = 0.4;
/** The side-to-side flutter on the way down builds up over this long (s). */
const SWAY_IN = 0.5;
/** Calm caps don't tumble: they rock gently about this tilt (rad), at this rate (rad/s). */
const ROCK_TILT = 0.7;
const ROCK_RATE = 1.3;

/** The tassel is a damped pendulum: its natural frequency (rad/s), air damping (/s), and how hard a spinning board drags the cord round (/s). */
const TASSEL_W = 8;
const TASSEL_DAMP = 0.6;
const TASSEL_GRIP = 4;
/** The speed, in window heights per second, at which the wind on the tassel matches its weight. */
const WIND = 1.2;

/** How many sparkles a cap gives off at the top of its throw. */
const SPARKLES = [1, 2, 2, 3];
/** Sparkles drift up a little as they fade, in px per second. */
const SPARKLE_RISE = 10;

interface Sparkle {
  x: number;
  y: number;
  /** Seconds since it appeared; negative while it waits its turn. */
  age: number;
  life: number;
  size: number;
  angle: number;
  spin: number;
}

interface Cap {
  /** Thrown this many ms in. */
  at: number;
  /** Where it's thrown from across the window, and how it drifts, in window widths (per second). */
  x0: number;
  drift: number;
  /** Heights as fractions of the window's, from the top: where it starts and ends, and the top of the throw. */
  start: number;
  peak: number;
  /** Seconds up to the peak and back down out of sight, and the launch speed in window heights per second. */
  rise: number;
  fall: number;
  speed: number;
  /** The board's width in px. */
  size: number;
  /** Turn in the plane of the screen, and its rate (rad, rad/s). */
  angle: number;
  spin: number;
  /** How far the board is tipped towards you (rad): its top shows while the sine is positive. Flipping turns it end over end. */
  tilt: number;
  flip: number;
  rock: number;
  rockPhase: number;
  /** The board's turn about its own centre, which makes it a square or a diamond in perspective. */
  yaw: number;
  yawRate: number;
  /** Side to side on the way down: in board widths, rad/s, rad. */
  sway: number;
  swayRate: number;
  swayPhase: number;
  /** The tassel's angle on screen (rad, π/2 is straight down) and how fast it's swinging. */
  swing: number;
  swingRate: number;
  /** Last frame's position in px, for the wind on the tassel. */
  x: number;
  y: number;
  thrown: boolean;
  peaked: boolean;
  gone: boolean;
  sparkles: Sparkle[];
  /** Shading round the skullcap, made on first draw. */
  shade: CanvasGradient | null;
}

/** Scratch for the board's corners, so drawing allocates nothing. */
const cornerX = [0, 0, 0, 0];
const cornerY = [0, 0, 0, 0];

const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, v));

function makeCaps(w: number, h: number, gentle: boolean): Cap[] {
  const volleys = gentle ? CALM_VOLLEYS : VOLLEYS;
  const n = gentle ? 6 : 16 + Math.floor(Math.random() * 5);
  const base = Math.min(64, Math.max(34, Math.min(w, h) * 0.06));
  const caps: Cap[] = [];
  for (let i = 0; i < n; i++) {
    const size = base * rand(0.75, 1.15);
    // One slot each across the width; neighbouring slots go in different volleys, so every volley spans the window.
    const x0 = 0.06 + (0.88 * (i + 0.5 + rand(-0.4, 0.4))) / n;
    const start = 1 + (REACH * size) / h;
    const peak = gentle ? rand(0.25, 0.45) : rand(0.15, 0.45);
    const rise = Math.sqrt((2 * (start - peak)) / RISE_G);
    const sparkles: Sparkle[] = [];
    for (let j = gentle ? 1 : pick(SPARKLES); j > 0; j--) {
      sparkles.push({ x: 0, y: 0, age: 0, life: rand(0.45, 0.75), size: size * rand(0.16, 0.28), angle: rand(0, Math.PI), spin: gentle ? 0 : rand(-1.5, 1.5) });
    }
    caps.push({
      at: volleys[i % volleys.length] + rand(0, STAGGER),
      x0,
      drift: (0.5 - x0) * 0.03 + rand(-0.012, 0.012),
      start,
      peak,
      rise,
      fall: rise / Math.sqrt(FALL_G),
      speed: RISE_G * rise,
      size,
      angle: rand(0, Math.PI * 2),
      spin: (gentle ? rand(0.25, 0.6) : rand(1.2, 3.2)) * pick([-1, 1]),
      tilt: gentle ? ROCK_TILT : rand(0, Math.PI * 2),
      flip: gentle ? 0 : rand(2.2, 4.8) * pick([-1, 1]),
      rock: gentle ? 0.3 : 0,
      rockPhase: rand(0, Math.PI * 2),
      yaw: rand(0, Math.PI * 2),
      yawRate: gentle ? rand(-0.2, 0.2) : rand(-0.8, 0.8),
      sway: gentle ? 0.35 : 0.7,
      swayRate: rand(3, 4.5),
      swayPhase: rand(0, Math.PI * 2),
      swing: Math.PI / 2 + rand(-0.3, 0.3),
      swingRate: 0,
      x: 0,
      y: 0,
      thrown: false,
      peaked: false,
      gone: false,
      sparkles,
      shade: null,
    });
  }
  // Smaller caps are further away, so they go underneath.
  return caps.sort((a, b) => a.size - b.size);
}

/** Its weight pulls the tassel down, the air pushes it against the cap's motion, and a spinning board drags its cord round. */
function swingTassel(c: Cap, vx: number, vy: number, h: number, sec: number) {
  const cord = CORD * c.size;
  const weight = TASSEL_W * TASSEL_W * cord;
  const wind = weight / (WIND * h);
  // Clamped, so a resize (which moves every cap at once) doesn't fling it.
  const fx = clamp(-vx * wind, weight * 1.2);
  const fy = weight + clamp(-vy * wind, weight * 1.2);
  const pull = (fy * Math.cos(c.swing) - fx * Math.sin(c.swing)) / cord;
  c.swingRate += (pull + TASSEL_GRIP * (c.spin - c.swingRate) - TASSEL_DAMP * c.swingRate) * sec;
  c.swing += c.swingRate * sec;
}

/** Shading across the skullcap, lighter towards one side so it looks round. It turns with the cap, which is near enough. */
function shade(ctx: CanvasRenderingContext2D, r: number) {
  const g = ctx.createLinearGradient(-r, 0, r, 0);
  g.addColorStop(0, SKULL_DARK);
  g.addColorStop(0.35, SKULL_LIGHT);
  g.addColorStop(1, SKULL_DARK);
  return g;
}

/**
 * The board, a thin square slab: squashed by `k` (the sine of its tilt) to put
 * it in perspective, its underside `up * THICK` below the top on screen. The
 * far face shows as an edge round the near one.
 */
function drawBoard(ctx: CanvasRenderingContext2D, c: Cap, k: number, up: number) {
  const s = c.size;
  const ca = Math.cos(c.yaw) * s * Math.SQRT1_2;
  const sa = Math.sin(c.yaw) * s * Math.SQRT1_2;
  cornerX[0] = ca;
  cornerY[0] = sa * k;
  cornerX[1] = -sa;
  cornerY[1] = ca * k;
  cornerX[2] = -ca;
  cornerY[2] = -sa * k;
  cornerX[3] = sa;
  cornerY[3] = -ca * k;

  // The slab's outline: the upper chain of whichever face is higher, down the sides, and the lower chain of the other.
  let left = 0;
  for (let i = 1; i < 4; i++) if (cornerX[i] < cornerX[left]) left = i;
  const right = (left + 2) % 4;
  const hi = cornerY[(left + 1) % 4] < cornerY[(left + 3) % 4] ? (left + 1) % 4 : (left + 3) % 4;
  const lo = (hi + 2) % 4;
  const under = THICK * s * up;
  const above = Math.min(0, under);
  const below = Math.max(0, under);
  ctx.beginPath();
  ctx.moveTo(cornerX[left], cornerY[left] + above);
  ctx.lineTo(cornerX[hi], cornerY[hi] + above);
  ctx.lineTo(cornerX[right], cornerY[right] + above);
  ctx.lineTo(cornerX[right], cornerY[right] + below);
  ctx.lineTo(cornerX[lo], cornerY[lo] + below);
  ctx.lineTo(cornerX[left], cornerY[left] + below);
  ctx.closePath();
  ctx.fillStyle = BOARD_EDGE;
  ctx.fill();

  const near = k > 0 ? 0 : under;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) ctx.lineTo(cornerX[i], cornerY[i] + near);
  ctx.closePath();
  ctx.fillStyle = k > 0 ? BOARD : BOARD_UNDER;
  ctx.fill();
  ctx.strokeStyle = RIM;
  ctx.lineWidth = 1;
  ctx.stroke();
}

/** The skullcap, a short cylinder under the board: two ellipses joined at the sides. From below you can see into it. */
function drawSkullcap(ctx: CanvasRenderingContext2D, c: Cap, k: number, up: number) {
  const s = c.size;
  const r = SKULL_R * s;
  const ry = r * Math.abs(k);
  const top = THICK * s * up;
  const rim = (THICK + SKULL_H) * s * up;
  // The outline takes the half of each end that faces away from the other end.
  const outward = up >= 0;
  ctx.beginPath();
  ctx.moveTo(-r, top);
  ctx.lineTo(-r, rim);
  ctx.ellipse(0, rim, r, ry, 0, Math.PI, 0, outward);
  ctx.lineTo(r, top);
  ctx.ellipse(0, top, r, ry, 0, 0, Math.PI, outward);
  ctx.closePath();
  c.shade ??= shade(ctx, r);
  ctx.fillStyle = c.shade;
  ctx.fill();
  ctx.strokeStyle = RIM;
  ctx.lineWidth = 1;
  ctx.stroke();
  if (k < 0) {
    ctx.beginPath();
    ctx.ellipse(0, rim, r * 0.86, ry * 0.86, 0, 0, Math.PI * 2);
    ctx.fillStyle = SKULL_INSIDE;
    ctx.fill();
  }
}

/** Cord from the button to a knot, bowing against the swing, then a tuft of strands trailing a little behind it. */
function drawTassel(ctx: CanvasRenderingContext2D, c: Cap, ax: number, ay: number) {
  const s = c.size;
  const cord = CORD * s;
  const dx = Math.cos(c.swing);
  const dy = Math.sin(c.swing);
  const kx = ax + dx * cord;
  const ky = ay + dy * cord;
  const bow = clamp(-c.swingRate * 0.04, 0.25) * cord;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.quadraticCurveTo(ax + dx * cord * 0.5 - dy * bow, ay + dy * cord * 0.5 + dx * bow, kx, ky);
  const trail = c.swing - c.swingRate * 0.05;
  for (let i = 0; i < 4; i++) {
    const a = trail + (i - 1.5) * 0.12;
    const len = TUFT * s * (i === 0 || i === 3 ? 0.88 : 1);
    ctx.moveTo(kx, ky);
    ctx.lineTo(kx + Math.cos(a) * len, ky + Math.sin(a) * len);
  }
  const width = Math.max(1.2, s * 0.035);
  ctx.strokeStyle = GOLD_DEEP;
  ctx.lineWidth = width + 1.5;
  ctx.stroke();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = width;
  ctx.stroke();
  dot(ctx, kx, ky, s * 0.05, GOLD_BRIGHT);
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = GOLD_DEEP;
  ctx.lineWidth = 1;
  ctx.stroke();
}

/**
 * One cap, centred on (x, y). Whichever of the skullcap and the tassel is
 * nearer you goes on top of the board: the tassel when you see the top, the
 * skullcap when you see the underside.
 */
function drawCap(ctx: CanvasRenderingContext2D, c: Cap, x: number, y: number, tilt: number) {
  const s = c.size;
  const k = Math.sin(tilt);
  const up = Math.cos(tilt);
  // The button sits on top of the board, so it's where the tassel hangs from whichever way up the cap is.
  const ax = x + BUTTON_H * s * up * Math.sin(c.angle);
  const ay = y - BUTTON_H * s * up * Math.cos(c.angle);
  if (k <= 0) drawTassel(ctx, c, ax, ay);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(c.angle);
  if (k > 0) drawSkullcap(ctx, c, k, up);
  drawBoard(ctx, c, k, up);
  if (k <= 0) drawSkullcap(ctx, c, k, up);
  ctx.restore();
  if (k > 0) {
    drawTassel(ctx, c, ax, ay);
    dot(ctx, ax, ay, s * 0.055, GOLD);
  }
}

/** A four-pointed glint that grows and shrinks over its life. */
function drawSparkle(ctx: CanvasRenderingContext2D, sp: Sparkle) {
  const r = sp.size * Math.sin((Math.PI * sp.age) / sp.life);
  const ux = Math.cos(sp.angle) * r;
  const uy = Math.sin(sp.angle) * r;
  const { x, y } = sp;
  ctx.beginPath();
  ctx.moveTo(x + ux, y + uy);
  ctx.quadraticCurveTo(x, y, x - uy, y + ux);
  ctx.quadraticCurveTo(x, y, x - ux, y - uy);
  ctx.quadraticCurveTo(x, y, x + uy, y - ux);
  ctx.quadraticCurveTo(x, y, x + ux, y + uy);
  ctx.fillStyle = GOLD_BRIGHT;
  ctx.fill();
  ctx.strokeStyle = GOLD_DEEP;
  ctx.lineWidth = 0.8;
  ctx.stroke();
}

function frame(caps: Cap[], { ctx, w, h, t, dt }: Frame): boolean {
  const sec = dt / 60;
  let alive = false;
  // A resize resets the context, so these go on every frame.
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  for (const c of caps) {
    if (c.gone) continue;
    alive = true;
    const tau = (t - c.at) / 1000;
    if (tau < 0) continue;
    const s = c.size;
    // Heights follow the throw exactly, so every cap lands on time at any frame rate.
    const down = Math.max(0, tau - c.rise);
    const fy = tau < c.rise ? c.start - c.speed * tau + 0.5 * RISE_G * tau * tau : c.peak + 0.5 * RISE_G * FALL_G * down * down;
    const x = (c.x0 + c.drift * tau) * w + c.sway * s * Math.min(1, down / SWAY_IN) * Math.sin(c.swayPhase + c.swayRate * down);
    const y = fy * h;
    if (down >= c.fall && y >= h + REACH * s) {
      c.gone = true;
      continue;
    }

    const vx = c.thrown ? (x - c.x) / sec : 0;
    const vy = c.thrown ? (y - c.y) / sec : 0;
    c.thrown = true;
    c.x = x;
    c.y = y;
    if (down > 0) {
      const settle = Math.exp(-SETTLE * sec);
      c.spin *= settle;
      c.flip *= settle;
    }
    c.angle += c.spin * sec;
    c.tilt += c.flip * sec;
    c.yaw += c.yawRate * sec;
    swingTassel(c, vx, vy, h, sec);

    if (!c.peaked && down > 0) {
      c.peaked = true;
      for (const sp of c.sparkles) {
        const a = rand(0, Math.PI * 2);
        const d = s * rand(0.5, 1.3);
        sp.x = x + Math.cos(a) * d;
        sp.y = y + Math.sin(a) * d * 0.7;
        sp.age = -rand(0, 0.25);
      }
    }
    drawCap(ctx, c, x, y, c.tilt + c.rock * Math.sin(c.rockPhase + tau * ROCK_RATE));
  }

  for (const c of caps) {
    if (!c.peaked) continue;
    for (const sp of c.sparkles) {
      if (sp.age >= sp.life) continue;
      sp.age += sec;
      if (sp.age >= sp.life) continue;
      alive = true;
      if (sp.age <= 0) continue;
      sp.y -= SPARKLE_RISE * sec;
      sp.angle += sp.spin * sec;
      drawSparkle(ctx, sp);
    }
  }
  return alive;
}

/** About 5 s of mortarboards; resolves once the last one has fallen out of sight. */
export default function play(): Promise<void> {
  const caps = makeCaps(innerWidth, innerHeight, calm());
  return scene((f) => frame(caps, f));
}
